import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { CustomInstructionValue } from '@components/forms/custom-instructions-form/custom-instructions-form.component';
import { SourcesButtonComponent } from '@components/sources/sources-button/sources-button.component';
import { environment } from '@environments/environment';
import { Idea, IdeaInputParams } from '@models/Sessions/Idea/Idea';
import {
  ScriptEvidence,
  UltimateGenerateRequestPromptLanguageEnum,
} from '@models/Sessions/Idea/podcast-api';
import { Session } from '@models/Sessions/Session';
import { PodcastService } from '@services/sessions/podcast.service';
import { MessageService } from 'primeng/api';
import { AutoFocusModule } from 'primeng/autofocus';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { map, mergeMap, Observable } from 'rxjs';

import { PromptLanguagePickerComponent } from '../components/prompt-language-picker/prompt-language-picker.component';
import { StartManuallyButtonComponent } from '../components/start-manually-button/start-manually-button.component';
import { NewIdeaService } from '../services/new-idea.service';
import { sourcesValidator } from '../validators/sources.validator';
import {
  CustomStyleSettingsComponent,
  CustomStyleSettingsData,
  CustomStyleSettingsPayload,
} from './custom-style-settings-dialog-content/custom-style-settings-dialog-content.component';

interface Form {
  title: FormControl<string | null>;
  customInstruction: FormControl<CustomInstructionValue>;
  sources: FormControl<string[]>;
  styleExamples: FormControl<string[]>;
  promptLanguage: FormControl<UltimateGenerateRequestPromptLanguageEnum>;
}

@Component({
  selector: 'app-new-custom-style',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ProgressBarModule,
    InputTextModule,
    AutoFocusModule,
    ButtonModule,
    DividerModule,
    StartManuallyButtonComponent,
    PromptLanguagePickerComponent,
    SourcesButtonComponent,
  ],
  providers: [DialogService],
  templateUrl: './new-custom-style.component.html',
  styleUrl: './new-custom-style.component.scss',
})
export class NewCustomStyleComponent implements OnInit {
  featureFlags = environment.featureFlags;
  isGenerating = signal(false);
  maxSourceItems = 5;
  maxStyleExamplesItems = 1;
  form!: FormGroup<Form>;
  dialogRef: DynamicDialogRef | undefined;

  @Input() topic: Session;

  get title(): string | null {
    return this.form.get('title').value;
  }

  get inputParams(): IdeaInputParams {
    const { title, customInstruction, sources, styleExamples, promptLanguage } =
      this.form.getRawValue();

    return {
      topic: title,
      context: customInstruction.content,
      promptLanguage,
      styleExamples: styleExamples,
      inputUrlOrTextList: sources,
    };
  }

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly formBuilder: FormBuilder,
    private readonly dialogService: DialogService,
    private readonly podcastService: PodcastService,
    private readonly ideaService: NewIdeaService,
    private readonly messageService: MessageService,
    private readonly router: Router,
  ) {}

  ngOnInit() {
    this.createForm();
  }

  private createForm() {
    this.form = this.formBuilder.group<Form>({
      title: this.formBuilder.control(''),
      customInstruction: this.formBuilder.control({
        name: '',
        content: '',
      }),
      sources: this.formBuilder.control<string[]>(
        [],
        sourcesValidator({ maxLength: 5 }),
      ),
      styleExamples: this.formBuilder.control<string[]>(
        [],
        sourcesValidator({ minLength: 1, maxLength: 1 }),
      ),
      promptLanguage:
        this.formBuilder.control<UltimateGenerateRequestPromptLanguageEnum>(
          'de',
          Validators.required,
        ),
    });
  }

  handleCustomizeClick() {
    // Metadata - use the same validation logic as the main Generate button
    const canGenerate = this.form.valid;

    const data: CustomStyleSettingsData = {
      customInstruction: this.form.get('customInstruction').value,
      meta: {
        canGenerate,
      },
    };

    this.dialogRef = this.dialogService.open(CustomStyleSettingsComponent, {
      modal: true,
      closable: true,
      contentStyle: { overflow: 'auto', width: '45rem' },
      header: 'Customize',
      closeOnEscape: true,
      dismissableMask: true,
      data,
    });

    this.dialogRef.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((payload?: CustomStyleSettingsPayload) => {
        if (!payload) {
          return;
        }
        this.form.patchValue(payload);
      });
  }

  handleSourcesChange(sources: string[]) {
    this.form.patchValue({
      sources,
    });
  }

  handleStyleExamples(styleExamples: string[]) {
    this.form.patchValue({
      styleExamples,
    });
  }

  handlePromptLanguageChange(lang: UltimateGenerateRequestPromptLanguageEnum) {
    this.form.patchValue({
      promptLanguage: lang,
    });
  }

  handleGenerateClick() {
    this.messageService.clear();
    this.isGenerating.set(true);

    this.generate()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        mergeMap(data =>
          this.ideaService
            .save(this.topic, data)
            .pipe(map((idea): [Idea, string] => [idea, data.inconsistency])),
        ),
      )
      .subscribe({
        next: ([createdIdea, inconsistency]) => {
          this.isGenerating.set(false);
          this.router.navigate(
            ['topics', this.topic._id, 'ideas', createdIdea._id],
            {
              queryParams: { edit: true },
              state: { inconsistency },
            },
          );
        },
        error: () => {
          this.isGenerating.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Something went wrong.',
            detail: 'Please try change input data or try later.',
            sticky: true,
          });
        },
      });
  }

  private generate(): Observable<{
    inputParams: IdeaInputParams;
    script: string;
    shortTitle?: string;
    oneLiner?: string;
    evidence: ScriptEvidence[];
    inconsistency?: string;
  }> {
    const { title, styleExamples, customInstruction, promptLanguage, sources } =
      this.form.getRawValue();

    const context = customInstruction.content;

    return this.podcastService
      .ultimateGenerate({
        document_type: 'custom',
        topic: title,
        sources,
        context,
        prompt_language: promptLanguage,
        user_text: styleExamples[0],
      })
      .pipe(
        map(data => ({
          inputParams: {
            topic: title,
            inputUrlOrTextList: sources,
            promptLanguage,
            styleExamples: styleExamples,
            context,
          },
          script: data.main_text,
          shortTitle: data.short_title,
          oneLiner: data.one_liner,
          evidence: data.evidence_info,
        })),
      );
  }
}
